import React, { useState } from "react"
import {
  Modal,
  Card,
  Space,
  Typography,
  MailOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
} from "@mimber/ui-web"
import { Resource, Action } from "@mimber/schema"
import { useOrganizationPermissions } from "@/core/hooks/useOrganizationPermissions"
import { CreateMemberForm } from "@/components/forms/CreateMemberForm"
import { SendInvitationForm } from "@/components/forms/SendInvitationForm"
import type { CreateMemberModalProps } from "./CreateMemberModal.types"

const { Text, Paragraph } = Typography

type AddMemberMode = "invite" | "managed" | "admin"

const MODE_TITLES: Record<AddMemberMode, string> = {
  invite: "Invite Member",
  managed: "Add Managed Member",
  admin: "Add Organization Admin",
}

export const CreateMemberModal: React.FunctionComponent<
  CreateMemberModalProps
> = ({ onSuccess, ...modalProps }) => {
  const [mode, setMode] = useState<AddMemberMode | null>(null)
  const { hasPermission } = useOrganizationPermissions()
  const canInvite = hasPermission(Resource.INVITATION, Action.CREATE)
  const canAddManaged = hasPermission(Resource.MEMBERSHIP, Action.CREATE)
  // Admin invites also POST to /invitations, so INVITATION:CREATE is required
  // in addition to the elevated role-management permissions.
  const canAddOrgAdmin =
    canInvite &&
    hasPermission(Resource.ROLE, Action.MANAGE) &&
    hasPermission(Resource.ORGANIZATION, Action.MANAGE)

  const handleSuccess = (close: () => void) => {
    close()
    onSuccess?.()
  }

  const handleModeCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    nextMode: AddMemberMode
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      setMode(nextMode)
    }
  }

  return (
    <Modal
      footer={null}
      title={mode ? MODE_TITLES[mode] : "Add Member"}
      afterClose={() => setMode(null)}
      {...modalProps}
    >
      {(close) => {
        if (!mode) {
          return (
            <Space
              direction="vertical"
              style={{ width: "100%", marginTop: 16 }}
              size="middle"
            >
              {canInvite && (
                <Card
                  hoverable
                  role="button"
                  tabIndex={0}
                  onClick={() => setMode("invite")}
                  onKeyDown={(event) => handleModeCardKeyDown(event, "invite")}
                >
                  <Space align="center">
                    <MailOutlined style={{ fontSize: 28, paddingRight: 8 }} />
                    <div>
                      <Text strong>Invite Member</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Send an email invitation so the person can create their
                        own Mimber account and manage their membership.
                      </Paragraph>
                    </div>
                  </Space>
                </Card>
              )}
              {canAddManaged && (
                <Card
                  hoverable
                  role="button"
                  tabIndex={0}
                  onClick={() => setMode("managed")}
                  onKeyDown={(event) => handleModeCardKeyDown(event, "managed")}
                >
                  <Space align="center">
                    <TeamOutlined style={{ fontSize: 28, paddingRight: 8 }} />
                    <div>
                      <Text strong>Add Managed Member</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        No email is sent and no account is created. You'll
                        manage this member's information on their behalf.
                      </Paragraph>
                    </div>
                  </Space>
                </Card>
              )}
              {canAddOrgAdmin && (
                <Card
                  hoverable
                  role="button"
                  tabIndex={0}
                  onClick={() => setMode("admin")}
                  onKeyDown={(event) => handleModeCardKeyDown(event, "admin")}
                >
                  <Space align="center">
                    <SafetyCertificateOutlined
                      style={{ fontSize: 28, paddingRight: 8 }}
                    />
                    <div>
                      <Text strong>Add Organization Admin</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Invite someone and grant them an elevated organization
                        role.
                      </Paragraph>
                    </div>
                  </Space>
                </Card>
              )}
            </Space>
          )
        }

        if (mode === "invite") {
          return <SendInvitationForm onSuccess={() => handleSuccess(close)} />
        }

        if (mode === "admin") {
          return (
            <SendInvitationForm
              isOrgAdminInvite
              onSuccess={() => handleSuccess(close)}
            />
          )
        }

        return (
          <CreateMemberForm
            onSuccess={() => handleSuccess(close)}
            onCancel={close}
          />
        )
      }}
    </Modal>
  )
}
